import { MentionUnavailable, takeTextMention } from "./MentionUnavailable";
import { useEffect, useRef, useState } from "react";
import { useValue } from "@tldraw/state-react";
import type { TLShapeId } from "@tldraw/tlschema";
import type { WorkspaceModel } from "./useWorkspace";
import { MentionPicker } from "./MentionPicker";
import { mentionsIn } from "../shared/mentions";
import { changeSignature } from "../shared/change-facts";
import { api, notify } from "./api";
import type { InboxItem } from "../shared/updates";
type Pending = { shape: string; occurrences: string[]; suppressed?: string[] };
const prefix = "calque:pending-mentions:";
export function clearMentionQueues() {
  for (const key of Object.keys(localStorage))
    if (key.startsWith(prefix)) localStorage.removeItem(key);
}
export function TextMentions({ model }: { model: WorkspaceModel }) {
  const { editor, room, user, connected } = model;
  const rich = useValue(
    "mention rich editor",
    () => editor.getRichTextEditor(),
    [editor],
  );
  const editing = useValue(
    "mention text source",
    () => editor.getEditingShape(),
    [editor],
  );
  const key = `${prefix}${user.id}:${room.id}`;
  const pending = useRef<Pending[]>([]),
    previous = useRef<{
      id: string;
      initial: ReturnType<typeof mentionsIn>;
    } | null>(null),
    busy = useRef(false);
  const [hint, setHint] = useState("");
  const [unavailable, setUnavailable] = useState<{
    item: InboxItem;
    reason: string;
  } | null>(null);
  const save = () => {
    try {
      localStorage.setItem(key, JSON.stringify(pending.current));
    } catch {
      notify(
        "Mention notifications could not be saved for retry. Keep this project open.",
      );
    }
  };
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? "[]");
      pending.current = Array.isArray(stored)
        ? stored
            .filter(
              (p) =>
                typeof p.shape === "string" &&
                Array.isArray(p.occurrences) &&
                p.occurrences.length <= 100,
            )
            .slice(0, 100)
        : [];
    } catch {
      pending.current = [];
    }
  }, [key]);
  useEffect(() => {
    const old = previous.current;
    if (old && old.id !== editing?.id) {
      const shape = editor.getShape(old.id as TLShapeId);
      if (shape?.type === "text") {
        const initialPeople = new Set(old.initial.map((m) => m.userId)),
          initialIds = new Set(old.initial.map((m) => m.occurrenceId));
        const occurrences = mentionsIn(shape.props.richText)
          .filter(
            (m) =>
              !initialPeople.has(m.userId) || initialIds.has(m.occurrenceId),
          )
          .map((m) => m.occurrenceId);
        const suppressed = mentionsIn(shape.props.richText)
          .filter((m) => !occurrences.includes(m.occurrenceId))
          .map((m) => m.occurrenceId);
        if (occurrences.length || suppressed.length) {
          pending.current = pending.current.filter((p) => p.shape !== shape.id);
          if (pending.current.length >= 100) {
            notify(
              "Mention notifications are delayed. Reopen this text after your connection recovers to send its mentions.",
            );
          } else
            pending.current.push({ shape: shape.id, occurrences, suppressed });
          save();
        }
      }
      setHint("");
    }
    if (editing?.type === "text" && old?.id !== editing.id)
      previous.current = {
        id: editing.id,
        initial: mentionsIn(editing.props.richText),
      };
    else if (editing?.type !== "text") previous.current = null;
  }, [editing?.id, editor]);
  useEffect(() => {
    if (!connected || room.platform !== "cloudflare") return;
    let live = true;
    const flush = async () => {
      if (busy.current || document.hidden) return;
      busy.current = true;
      try {
        for (const p of [...pending.current]) {
          if (!live) break;
          if (editor.getEditingShapeId() === p.shape) continue;
          const shape = editor.getShape(p.shape as TLShapeId);
          if (shape?.type !== "text") {
            pending.current = pending.current.filter((x) => x !== p);
            save();
            continue;
          }
          const occurrences = p.occurrences.filter((id) =>
            mentionsIn(shape.props.richText).some((m) => m.occurrenceId === id),
          );
          const res = await fetch(`/api/rooms/${room.id}/text-mentions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...p,
              occurrences,
              signature: changeSignature(shape),
            }),
          });
          if (res.ok) {
            pending.current = pending.current.filter((x) => x !== p);
            save();
            setHint("");
          } else if ([401, 403, 404].includes(res.status)) {
            pending.current = pending.current.filter((x) => x !== p);
            save();
            notify(
              "Mention not sent: the text or a member is no longer available.",
            );
          } else {
            setHint("Mention notification pending · retrying");
            break;
          }
        }
      } catch {
        if (live) setHint("Mention notification pending · retrying");
      } finally {
        busy.current = false;
      }
    };
    const timer = setInterval(() => void flush(), 1500);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [connected, editor, key, room.id, room.platform]);
  // Navigation is driven by the same App route event as discussions, with an explicit text target.
  useEffect(() => {
    if (!connected) return;
    const shapeId = new URLSearchParams(location.search).get("mentionText");
    if (!shapeId) return;
    const shape = editor.getShape(shapeId as TLShapeId);
    if (!shape) return;
    const url = new URL(location.href);
    url.searchParams.delete("mentionText");
    history.replaceState(history.state, "", url);
    const item = takeTextMention(room.id, shapeId);
    if (editor.isShapeHidden(shape)) {
      if (item)
        setUnavailable({ item, reason: "This text is on a hidden layer" });
      else notify("This text is on a hidden layer.");
      return;
    }
    if (
      item &&
      shape.type === "text" &&
      !mentionsIn(shape.props.richText).some(
        (m) =>
          m.userId === user.id &&
          JSON.parse(item.source_occurrences ?? "[]").includes(m.occurrenceId),
      )
    )
      setUnavailable({
        item,
        reason: "This mention was removed from the text",
      });
    const bounds = editor.getShapePageBounds(shape);
    if (!bounds) return;
    editor.setCurrentPage(editor.getAncestorPageId(shape)!);
    editor.select(shape.id);
    editor.zoomToBounds(bounds, {
      inset: 160,
      targetZoom: Math.max(1, editor.getZoomLevel()),
      animation: { duration: 200 },
    });
  }, [connected, editor, room]);
  useTextMentionSeen(model);
  if (room.platform !== "cloudflare") return null;
  return (
    <>
      {unavailable && (
        <MentionUnavailable
          {...unavailable}
          close={() => setUnavailable(null)}
        />
      )}{" "}
      {editing?.type === "text" && model.canEdit && (
        <MentionPicker
          rich={rich}
          people={model.people}
          error={model.peopleError}
          onRetry={model.retryPeople}
          onInsert={(m) =>
            setHint(`${m.label} will be notified when you finish editing`)
          }
        />
      )}{" "}
      {hint && (
        <div role="status" className="mention-pending-hint">
          {hint}
        </div>
      )}
    </>
  );
}
function useTextMentionSeen({ editor, room, user, connected }: WorkspaceModel) {
  useEffect(() => {
    if (!connected || room.platform !== "cloudflare") return;
    let live = true,
      busy = false,
      next = 0;
    let items: InboxItem[] = [];
    let cursor: string | null = null;
    const since = new Map<string, number>();
    const exposures = new Map<string, string>();
    const tick = async () => {
      if (!live || busy || document.hidden || !document.hasFocus()) {
        since.clear();
        return;
      }
      busy = true;
      try {
        if (Date.now() >= next) {
          const page = await api<{ items: InboxItem[]; cursor: string | null }>(
            `/api/updates?project=${room.id}&text=1&unread=1${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
          );
          items = page.items;
          cursor = page.cursor;
          since.clear();
          exposures.clear();
          next = Date.now() + 5000;
        }
        if (!live) return;
        for (const item of items) {
          const shape = editor.getShape(item.target_id as TLShapeId);
          const dom =
            shape &&
            [...document.querySelectorAll<HTMLElement>("[data-shape-id]")].find(
              (el) => el.dataset.shapeId === shape.id,
            );
          const occurrenceIds: string[] = JSON.parse(
            item.source_occurrences ?? "[]",
          );
          const matchesSource =
            shape?.type === "text" &&
            mentionsIn(shape.props.richText).some(
              (m) =>
                m.userId === user.id && occurrenceIds.includes(m.occurrenceId),
            );
          const tokens = dom
            ? [...dom.querySelectorAll<HTMLElement>("[data-mention]")].filter(
                (el) =>
                  el.dataset.mention === user.id &&
                  occurrenceIds.includes(el.dataset.mentionOccurrence ?? ""),
              )
            : [];
          const visible =
            matchesSource &&
            shape?.type === "text" &&
            !editor.isShapeHidden(shape) &&
            editor.getAncestorPageId(shape) === editor.getCurrentPageId() &&
            tokens.some((el) => {
              const b = el.getBoundingClientRect();
              return (
                b.height >= 12 &&
                b.width >= 12 &&
                b.left >= 0 &&
                b.top >= 100 &&
                b.right <= innerWidth &&
                b.bottom <= innerHeight - 50 &&
                el.contains(
                  document.elementFromPoint(
                    b.x + b.width / 2,
                    b.y + b.height / 2,
                  ),
                )
              );
            });
          const k = `${item.id}:${item.updated_at}`;
          const position = dom?.getBoundingClientRect();
          const exposure = `${shape ? changeSignature(shape) : ""}:${position?.x}:${position?.y}:${position?.width}`;
          if (exposures.get(k) !== exposure) {
            since.delete(k);
            exposures.set(k, exposure);
          }
          if (!visible) {
            since.delete(k);
            continue;
          }
          if (!since.has(k)) {
            since.set(k, Date.now());
            continue;
          }
          if (Date.now() - since.get(k)! < 1000) continue;
          await api("/api/updates/read", {
            id: item.id,
            board: room.id,
            version: item.updated_at,
          });
          if (!live) return;
          await api(`/api/rooms/${room.id}/changes`, {
            action: "interact",
            records: [{ id: shape!.id, signature: changeSignature(shape!) }],
          });
          items = items.filter((x) => x.id !== item.id);
          window.dispatchEvent(new Event("planroom:updates-read"));
          window.dispatchEvent(new Event("planroom:changes-seen"));
        }
      } catch {
        next = Date.now() + 5000;
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(() => void tick(), 500);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [editor, room.id, room.platform, user.id, connected]);
}
